/**
 * Facility-scoped SMS/WhatsApp retry configuration.
 */

import { API_ENDPOINTS } from '../config/api.config';
import { apiDelete, apiGet, apiPost, apiPut, ApiError } from '../utils/apiClient';
import type { NotificationRetryConfig } from '../types/notificationRetryConfig.types';

interface ApiListResponse {
  success?: boolean;
  message?: string;
  data?: NotificationRetryConfig[];
}

interface ApiSingleResponse {
  success?: boolean;
  message?: string;
  data?: NotificationRetryConfig;
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.response && typeof error.response === 'object') {
      const response = error.response as { message?: string; error?: string; statusCode?: number };
      if (error.statusCode === 409) {
        return (
          response.message ||
          'A retry configuration already exists for this facility. Edit the existing record instead.'
        );
      }
      if (response.message) {
        return response.message;
      }
      if (response.error) {
        return response.error;
      }
    }
    return error.message;
  }
  return error instanceof Error ? error.message : 'Notification retry config request failed';
}

class NotificationRetryConfigService {
  async list(facilityId: number): Promise<{
    success: boolean;
    data?: NotificationRetryConfig[];
    error?: string;
  }> {
    try {
      const response = await apiGet<ApiListResponse>(
        API_ENDPOINTS.NOTIFICATION_RETRY_CONFIG.LIST(facilityId)
      );
      const body = response.data;
      if (!body?.success) {
        return {
          success: false,
          error: body?.message || 'Failed to load notification retry config',
        };
      }
      return { success: true, data: Array.isArray(body.data) ? body.data : [] };
    } catch (error) {
      return { success: false, error: extractErrorMessage(error) };
    }
  }

  async create(payload: {
    facilityId: number;
    maxRetryCount: number;
    retryIntervalMinutes: number;
  }): Promise<{ success: boolean; data?: NotificationRetryConfig; error?: string }> {
    try {
      const response = await apiPost<ApiSingleResponse>(
        API_ENDPOINTS.NOTIFICATION_RETRY_CONFIG.CREATE,
        payload
      );
      const body = response.data;
      if (!body?.success || !body.data) {
        return {
          success: false,
          error: body?.message || 'Failed to create notification retry config',
        };
      }
      return { success: true, data: body.data };
    } catch (error) {
      return { success: false, error: extractErrorMessage(error) };
    }
  }

  async update(
    id: number,
    payload: { facilityId: number; maxRetryCount: number; retryIntervalMinutes: number }
  ): Promise<{ success: boolean; data?: NotificationRetryConfig; error?: string }> {
    try {
      const response = await apiPut<ApiSingleResponse>(
        API_ENDPOINTS.NOTIFICATION_RETRY_CONFIG.UPDATE(id),
        payload
      );
      const body = response.data;
      if (!body?.success || !body.data) {
        return {
          success: false,
          error: body?.message || 'Failed to update notification retry config',
        };
      }
      return { success: true, data: body.data };
    } catch (error) {
      return { success: false, error: extractErrorMessage(error) };
    }
  }

  async remove(id: number, facilityId: number): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await apiDelete<{ success?: boolean; message?: string }>(
        API_ENDPOINTS.NOTIFICATION_RETRY_CONFIG.DELETE(id, facilityId)
      );
      const body = response.data;
      if (!body?.success) {
        return {
          success: false,
          error: body?.message || 'Failed to delete notification retry config',
        };
      }
      return { success: true };
    } catch (error) {
      return { success: false, error: extractErrorMessage(error) };
    }
  }
}

export const notificationRetryConfigService = new NotificationRetryConfigService();
export default notificationRetryConfigService;
